/**
 * SavingsCalculatorSection — embeddable "Savings + Comparison" experience.
 *
 * iter132 — Restaurant Benefits (/for-restaurants) now consolidates the
 * old "Why NoFee", "Restaurant Benefits" and "Pricing" pages into ONE
 * hub. This component owns the calculator + marketplace comparison
 * blocks so both the canonical /savings-calculator page and the new
 * /for-restaurants hub render the exact same interactive experience
 * without duplicating markup.
 *
 * Scope: everything BETWEEN the standalone hero and the site Footer
 *   → interactive calculator (2 inputs + competitor selector + results)
 *   → "Real Cost of Marketplaces" bullet intro
 *   → Marketplaces vs NoFee comparison cards
 *
 * Not included (kept on the standalone /savings-calculator page for SEO
 * depth): the "Fast by NoFee" operational block and the Dashboard
 * Benefits closing block. Those don't belong in the merged hub because
 * ForRestaurants already renders its own dedicated Fast + Benefits
 * sections above the calculator.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  TrendingDown,
  ShieldCheck,
  Check,
  Minus,
} from 'lucide-react';

const COMPETITORS = [
  { id: 'typical', label: 'Typical Marketplace', rate: 30 },
  { id: 'premium', label: 'Premium Marketplace', rate: 30 },
  { id: 'highfee', label: 'High-Fee Marketplace', rate: 28 },
  { id: 'custom', label: 'Custom Commission', rate: 25 },
];

const NOFEE_PRICE = 299;

const formatMoney = (n) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const SliderRow = ({ label, value, onChange, min, max, step, unit, testId }) => (
  <div data-testid={`field-${testId}`}>
    <div className="flex items-baseline justify-between mb-2">
      <label className="text-[12px] uppercase tracking-[0.22em] text-white/55 font-bold">
        {label}
      </label>
      <span className="text-2xl sm:text-3xl font-black text-white tabular-nums">
        {unit === '$' ? formatMoney(value) : value.toLocaleString('en-US')}
        {unit && unit !== '$' ? (
          <span className="text-white/40 text-base ml-1">{unit}</span>
        ) : null}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={`slider-${testId}`}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 accent-gold-500"
    />
  </div>
);

const Stat = ({ label, value, muted = false, testId }) => (
  <div
    className={`rounded-2xl border ${
      muted
        ? 'border-white/[0.06] bg-white/[0.02]'
        : 'border-gold-500/20 bg-gold-500/[0.03]'
    } px-4 py-3.5`}
    data-testid={`stat-${testId}`}
  >
    <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold">
      {label}
    </p>
    <p
      className={`mt-1 text-xl sm:text-2xl font-black tabular-nums ${
        muted ? 'text-white/70' : 'text-white'
      }`}
    >
      {value}
    </p>
  </div>
);

const ComparisonCard = ({ title, rate, bullets, isLoss = false }) => (
  <div
    className={`relative rounded-3xl border p-7 sm:p-8 ${
      isLoss
        ? 'border-white/10 bg-zinc-950'
        : 'border-gold-500/30 bg-gradient-to-b from-zinc-950 to-black shadow-[0_30px_80px_-30px_rgba(212,175,55,0.25)]'
    }`}
  >
    <div className="flex items-baseline justify-between mb-6">
      <h3 className="text-2xl font-black text-white">{title}</h3>
      {isLoss ? (
        <div className="flex flex-col items-end leading-none" data-testid="rate-up-to">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-1.5">
            Up to
          </span>
          <span className="text-3xl font-black tabular-nums text-rose-300/80">
            {rate}
          </span>
        </div>
      ) : (
        <span className="text-3xl font-black tabular-nums text-gold-400">
          {rate}
        </span>
      )}
    </div>
    <ul className="space-y-3">
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-3 text-[14px] text-white/75">
          {isLoss ? (
            <Minus className="w-4 h-4 text-rose-400/70 flex-shrink-0 mt-0.5" />
          ) : (
            <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
          )}
          <span>{b}</span>
        </li>
      ))}
    </ul>
    {!isLoss && (
      <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2 text-[12px] text-white/55">
        <ShieldCheck className="w-3.5 h-3.5 text-gold-400" />
        <span>Secure direct payouts · Stripe</span>
      </div>
    )}
  </div>
);

/**
 * SavingsCalculatorSection
 *
 * Props:
 * - primaryCtaTo  (optional) — target route for the "stop losing X /
 *   month" button. Default `/onboarding/account` so the merged hub
 *   ("Create Account") never re-opens the legacy /start flow.
 * - showComparison (optional) — when false, only renders the
 *   interactive calculator (used by ForRestaurants when a separate
 *   comparison section already sits above/below). Default true.
 */
const SavingsCalculatorSection = ({
  primaryCtaTo = '/onboarding/account',
  showComparison = true,
}) => {
  const [orders, setOrders] = useState(300);
  const [ticket, setTicket] = useState(30);
  const [competitor, setCompetitor] = useState(COMPETITORS[0]);
  const [customRate, setCustomRate] = useState(25);

  const rate = competitor.id === 'custom' ? customRate : competitor.rate;

  const calc = useMemo(() => {
    const monthlyRevenue = orders * ticket;
    const monthlyLoss = monthlyRevenue * (rate / 100);
    const yearlyLoss = monthlyLoss * 12;
    const monthlySaved = monthlyLoss - NOFEE_PRICE;
    const yearlySaved = monthlySaved * 12;
    const dinners = Math.max(0, Math.floor(monthlySaved / 75));
    return {
      monthlyRevenue,
      monthlyLoss,
      yearlyLoss,
      monthlySaved: Math.max(0, monthlySaved),
      yearlySaved: Math.max(0, yearlySaved),
      dinners,
    };
  }, [orders, ticket, rate]);

  return (
    <>
      {/* Calculator */}
      <section
        className="relative pb-16 lg:pb-20"
        data-testid="calculator"
      >
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-7 sm:p-8 space-y-7">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                Your restaurant
              </p>

              <SliderRow
                label="Orders per month"
                value={orders}
                onChange={setOrders}
                min={50}
                max={2000}
                step={10}
                testId="orders"
              />

              <SliderRow
                label="Average ticket"
                value={ticket}
                onChange={setTicket}
                min={10}
                max={120}
                step={1}
                unit="$"
                testId="ticket"
              />

              <div data-testid="field-competitor">
                <p className="text-[12px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">
                  Current marketplace
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {COMPETITORS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCompetitor(c)}
                      data-testid={`competitor-${c.id}`}
                      className={`px-3 py-3 rounded-2xl border text-left transition-all ${
                        competitor.id === c.id
                          ? 'border-gold-500/60 bg-gold-500/[0.06]'
                          : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                      }`}
                    >
                      <div
                        className={`text-[13px] font-bold ${
                          competitor.id === c.id ? 'text-white' : 'text-white/75'
                        }`}
                      >
                        {c.label}
                      </div>
                      <div className="text-[11px] text-white/45 mt-0.5 tabular-nums">
                        {c.id === 'custom'
                          ? `${customRate}% commission`
                          : `${c.rate}% commission`}
                      </div>
                    </button>
                  ))}
                </div>
                {competitor.id === 'custom' && (
                  <div className="mt-4">
                    <SliderRow
                      label="Custom commission %"
                      value={customRate}
                      onChange={setCustomRate}
                      min={5}
                      max={40}
                      step={1}
                      unit="%"
                      testId="custom-rate"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Output */}
            <div
              className="lg:col-span-3 relative rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-7 sm:p-10 overflow-hidden"
              data-testid="results"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(212,175,55,0.10),transparent_60%)]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                    Estimated savings with NoFee
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-bold">
                    Per year
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div
                    className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent tabular-nums leading-none"
                    data-testid="yearly-savings"
                  >
                    {formatMoney(calc.yearlySaved)}
                  </div>
                  <div
                    className="flex items-center gap-1.5 text-[12px] text-rose-300/80"
                    data-testid="yearly-loss-sublabel"
                  >
                    <TrendingDown className="w-3 h-3" />
                    <span className="tabular-nums">
                      Currently lost to commissions: {formatMoney(calc.yearlyLoss)}/yr
                    </span>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
                  <Stat
                    label="Monthly savings"
                    value={formatMoney(calc.monthlySaved)}
                    testId="monthly-savings"
                  />
                  <Stat
                    label="Lost / month"
                    value={formatMoney(calc.monthlyLoss)}
                    muted
                    testId="monthly-loss"
                  />
                  <Stat
                    label="Effective rate"
                    value={`${rate}%`}
                    muted
                    testId="effective-rate"
                  />
                </div>

                {calc.dinners > 0 && (
                  <p className="mt-7 text-[13.5px] text-white/65 leading-relaxed border-t border-white/[0.06] pt-6">
                    <span className="text-white font-bold">
                      That&apos;s {calc.dinners} family dinners
                    </span>{' '}
                    worth of margin every month — going back into your business instead of a marketplace.
                  </p>
                )}

                <div className="mt-7">
                  <Link
                    to={primaryCtaTo}
                    data-testid="calc-cta-primary"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
                  >
                    Stop losing {formatMoney(calc.monthlyLoss)} / month
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <p className="mt-4 text-[11px] text-white/35 leading-relaxed">
                  Estimates based on $299/mo NoFee subscription. 0% commission · 45-day free trial · no contracts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showComparison && (
        <section className="relative pb-12 lg:pb-16">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                The hidden math
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
                The Real Cost of Marketplaces
              </h2>
              <p className="mt-4 text-white/65 leading-relaxed text-[15px]">
                Most restaurants focus only on commission fees. The real cost often includes:
              </p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-left text-[14px] text-white/75 max-w-xl mx-auto">
                {[
                  'Per-order commissions',
                  'Payout delays',
                  'Payout acceleration fees',
                  'Promotional fees',
                  'Visibility fees',
                  'Loss of customer ownership',
                  'Loss of customer data',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Minus className="w-3.5 h-3.5 text-rose-400/70 flex-shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[14.5px] text-white/65 leading-relaxed">
                NoFee helps restaurants keep more revenue while maintaining full control of their business.
              </p>
            </div>

            <div className="text-center mb-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                Side by side
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
                Marketplaces vs. NoFee
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ComparisonCard
                title={competitor.label}
                rate={`${rate}%`}
                isLoss
                bullets={[
                  'High per-order commissions',
                  'Additional payout acceleration fees',
                  'Additional promotional fees',
                  'Additional visibility fees',
                  'Delayed payout schedules',
                  'Marketplace owns customer relationship',
                  'Marketplace owns customer data',
                  'Customer loyalty belongs to marketplace',
                  'No Fast Score',
                  'Verified on-time delivery',
                  'No customer-verified delivery ranking',
                  'Limited branding control',
                  'Limited direct communication with customers',
                ]}
              />
              <ComparisonCard
                title="NoFee"
                rate="0%"
                bullets={[
                  '0% commission on orders',
                  'Direct Stripe payouts',
                  'Full payout visibility',
                  'Faster access to your revenue',
                  'Restaurant owns customer relationship',
                  'Restaurant owns customer data',
                  'Restaurant owns loyalty program',
                  'Branded ordering page',
                  '⚡ Fast by NoFee',
                  'Verified on-time delivery',
                  'Customer-verified on-time delivery',
                  'Delivery performance dashboard',
                  'Direct communication with customers',
                ]}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default SavingsCalculatorSection;
